/**
 * Componente: QueueItemsTable
 * Exibe tabela de itens da fila com linhas expansíveis
 * Mostra payload, steps de execução e informações detalhadas
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

/**
 * Interface para item da fila compatível com PGMQ
 */
export interface QueueItemPgmq {
  id: string;
  queue_type: string;
  status: string;
  attempts: number;
  max_attempts: number;
  created_at: string;
  processed_at: string | null;
  error_message: string | null;
  payload: Record<string, unknown>;
  provider_slug: string | null;
  tenant_id: string | null;
  tenant_name?: string;
  flow_name?: string;
  processing_time?: number;
  contact_phone?: string;
}
import { toast } from 'sonner';
import { QueueItemRow } from './QueueItemRow';

interface QueueItemsTableProps {
  items: QueueItemPgmq[] | undefined;
  isLoading?: boolean;
  isRefreshing?: boolean;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  onRefresh: () => void;
  duplicatedConversations?: Map<string, number>;
}

/**
 * Componente que exibe tabela de itens da fila com linhas expansíveis
 */
export function QueueItemsTable({ 
  items, 
  isLoading,
  isRefreshing,
  statusFilter,
  onStatusFilterChange,
  onRefresh,
  duplicatedConversations 
}: QueueItemsTableProps) {
  const [retryingId, setRetryingId] = useState<string | null>(null);

  // Função para reprocessar item falhado
  const handleRetry = async (itemId: string) => {
    setRetryingId(itemId);
    try {
      const { error } = await supabase
        .from('flow_queue')
        .update({ 
          status: 'pending',
          attempts: 0,
          error_message: null,
          processed_at: null,
        })
        .eq('id', itemId);

      if (error) throw error;

      toast.success('Item reenfileirado para processamento');
      onRefresh();
    } catch (error) {
      console.error('Erro ao reprocessar item:', error);
      toast.error('Erro ao reprocessar item');
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Itens Recentes da Fila</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={onStatusFilterChange}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filtrar status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendentes</SelectItem>
                <SelectItem value="processing">Processando</SelectItem>
                <SelectItem value="completed">Completados</SelectItem>
                <SelectItem value="failed">Falhas</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={onRefresh} disabled={isRefreshing}>
              <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : !items || items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Nenhum item encontrado
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Duplic</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Flow</TableHead>
                  <TableHead>Tentativas</TableHead>
                  <TableHead>Criado</TableHead>
                  <TableHead>Tempo</TableHead>
                  <TableHead className="w-[100px]">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <QueueItemRow
                    key={item.id}
                    item={item}
                    onRetry={handleRetry}
                    retrying={retryingId === item.id}
                    duplicatedConversations={duplicatedConversations}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
